import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { Appointment } from '../../models/appointment.model';
import { ApiService } from '../../services/api.service';
import { DEFAULT_TIME_SLOTS } from '../../shared/constants/time-slots.constant';
import { LocationApiResponse } from '../../shared/types';

// Location Hours Interface
interface LocationHours {
  id: string;
  location_id: string;
  shop_id: string;
  day: string;
  open_time: string;
  close_time: string;
  is_closed: boolean;
  status: boolean;
  created_at: string;
  updated_at: string;
}

/** Extends the shared LocationApiResponse with business-hours data returned by the locations detail endpoint. */
interface LocationDetails extends LocationApiResponse {
  hours: LocationHours[];
}

/**
 * Modal component for rescheduling an existing appointment. Presents a month calendar and time slots, respects location operating hours and off-days, allows drag-and-drop preselection, and emits an update payload when the appointment is confirmed.
 * @component
 * @selector app-reschedule-appointment-modal
 * @example
 *   <app-reschedule-appointment-modal
 *     [isVisible]="true"
 *     [appointment]="selectedAppointment"
 *     [droppedDate]="draggedDate"
 *     [loading]="false"
 *     (closeModal)="handleClose()"
 *     (updateAppointment)="handleUpdate($event)">
 *   </app-reschedule-appointment-modal>
 *
 * Inputs:
 * @input {boolean} isVisible - Controls modal visibility.
 * @input {Appointment | null} appointment - The current appointment object to be rescheduled (used to initialize selected date/time and build update payload).
 * @input {Date | null} droppedDate - An optional date provided via drag-and-drop to preselect a new date.
 * @input {boolean} loading - Flags whether operations are in progress (used to disable interactions).
 *
 * Outputs:
 * @output {EventEmitter<void>} closeModal - Emitted when the modal is closed without updating.
 * @output {EventEmitter<{appointmentId: string, payload: any}>} updateAppointment - Emitted when the user confirms a reschedule; provides the appointment id and the update payload (appointment_time, employee_id, customer_id, vehicle_id, package_id, location_id, source, duration, appointment_status).
 *
 * Key responsibilities and behavior:
 * - Initializes and renders a 6-week calendar view for the currentMonth and marks off-days / past days.
 * - Loads location details and operating hours from the API to determine available dates and time slots.
 * - Supports selecting a date and time slot, validating availability against operating hours.
 * - Prevents selection of past dates and fully closed days; displays an off-days confirmation modal if user attempts to schedule on a closed day.
 * - Produces a standardized payload and emits updateAppointment when proceeding with a valid reschedule.
 * - Resets internal selection state and emits closeModal when closed.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Generates calendar, applies any droppedDate, initializes selection from the current appointment, and fetches location details.
 * - ngOnChanges(changes: SimpleChanges): Reacts to changes in droppedDate or appointment to reinitialize selection and refetch location details as needed.
 *
 * Key methods:
 * - generateCalendarDays(): Builds the 6x7 calendar grid for the currentMonth including flags for isCurrentMonth, isOffDay, and isPastDate.
 * - getCurrentMonthYear(): Returns a localized month/year label for the currentMonth.
 * - previousMonth(), nextMonth(): Navigate calendar months and regenerate days.
 * - isOffDay(date: Date): Determines if a date is closed based on fetched locationHours.
 * - isPastDate(date: Date): Checks whether a date is before today (disables past selection).
 * - isDateDisabled(date: Date): Composite check for disabling a date (past or off-day).
 * - onDateSelect(date: Date), onTimeSelect(slot): Handle user selection of date and time and update selectedDateTime.
 * - isTimeSlotAvailable(slot, selectedDate): Validates a time slot against the operating hours of the selected day.
 * - convertTimeToMinutes(timeString: string): Utility to compare times in minutes for open/close comparisons.
 * - updateSelectedDateTime(): Combine selectedDate and selectedTime into selectedDateTime and invoke onDateTimeChange.
 * - hasDateTimeChanged(): Compare selected date/time with the original appointment date/time.
 * - onClose(): Reset selection state and emit closeModal.
 * - onUpdate(): Validate selection, show off-days modal if necessary, or call proceedWithUpdate() to emit the update.
 * - proceedWithUpdate(): Constructs the update payload (including formatted ISO appointment_time and duration) and emits updateAppointment with appointment id and payload.
 * - fetchLocationDetails(): Loads location details and hours from API and regenerates the calendar with location-specific constraints.
 * - getOperatingHoursForDay(date: Date): Returns LocationHours for a given weekday or null if not available.
 * - getAppointmentDuration(): Formats the appointment duration (hours/minutes) into a human-readable string used in the payload.
 *
 * Notes:
 * - The component expects an Appointment object structure containing appointment_time (ISO string), id, employee_id, customer_id, vehicle_id, package_id, location_id, and duration ({hours, minutes}).
 * - The update payload defaults duration to '2 hours' when appointment.duration is absent.
 * - The component guards against performing updates when selectedDateTime or appointment is missing and warns via console in such cases.
 */
@Component({
  selector: 'app-reschedule-appointment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './reschedule-appointment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reschedule-appointment-modal.component.scss'],
})
export class RescheduleAppointmentModalComponent implements OnInit, OnChanges {
  @Input() isVisible = false;
  @Input() appointment: Appointment | null = null;
  @Input() droppedDate: Date | null = null;
  @Input() loading = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() updateAppointment = new EventEmitter<{ appointmentId: string; payload: any }>();

  selectedDateTime: Date | null = null;
  originalDateTime: Date | null = null; // Track the original appointment date/time

  // Off-days modal properties
  showOffDaysModal = false;
  pendingUpdate = false;

  // Location details
  locationDetails: LocationDetails | null = null;
  locationHours: LocationHours[] = [];

  // Calendar properties
  weekDays: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  calendarDays: any[] = [];
  currentMonth: Date = new Date();
  shopToday: Date | null = null;
  selectedDate: Date | null = null;
  selectedTime = '';

  // Time slots for the date picker
  timeSlots = [...DEFAULT_TIME_SLOTS];

  constructor(private apiService: ApiService) {}

  get showShopTimezoneWarning(): boolean {
    const shopTimezone = this.locationDetails?.timezone;
    if (!shopTimezone) {
      return false;
    }

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !!browserTimezone && shopTimezone !== browserTimezone;
  }

  ngOnInit(): void {
    this.generateCalendarDays();
    this.handleDroppedDate();
    this.initializeWithCurrentAppointment();
    this.fetchLocationDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['droppedDate'] && changes['droppedDate'].currentValue) {
      this.handleDroppedDate();
    }
    if (changes['appointment'] && changes['appointment'].currentValue) {
      this.initializeWithCurrentAppointment();
      this.fetchLocationDetails();
    }
  }

  // Fetch location details and hours
  /**
   * Fetches location details for the current appointment, updates component state, and regenerates the calendar.
   * @example
   * this.fetchLocationDetails()
   * undefined
   * @param {void} none - This method accepts no parameters.
   * @returns {void} Does not return a value; updates locationDetails, locationHours, and calls generateCalendarDays().
   */
  private fetchLocationDetails(): void {
    if (!this.appointment?.location_id) {
      console.warn('No location ID available for appointment');
      return;
    }

    this.apiService.get(`location/view-location/${this.appointment.location_id}`).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.locationDetails = response.data;
          this.locationHours = response.data.hours || [];
          this.shopToday = this.getShopToday();
          if (!this.selectedDate && this.shopToday) {
            this.currentMonth = new Date(
              this.shopToday.getFullYear(),
              this.shopToday.getMonth(),
              1
            );
          }

          // Regenerate calendar with location hours
          this.generateCalendarDays();
        }
      },
      error: error => {
        console.error('Error fetching location details:', error);
      },
    });
  }

  // Check if a date is on a closed day based on location hours
  isOffDay(date: Date): boolean {
    const dayHours = this.getOperatingHoursForDay(date);

    if (!dayHours) {
      // If no location hours available, don't disable any days
      return false;
    }

    return dayHours.is_closed;
  }

  // Check if a date is in the past (before today)
  isPastDate(date: Date): boolean {
    const today = this.getShopToday();
    if (!today) {
      return false;
    }
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  }

  // Check if a date should be disabled (past date or off day)
  isDateDisabled(date: Date): boolean {
    if (!this.getLocationTimezone() || this.locationHours.length === 0) {
      return true;
    }
    return this.isPastDate(date) || this.isOffDay(date) || !this.hasAvailableTimeSlot(date);
  }

  // Calendar methods
  /**
   * Populate this.calendarDays with a 6-week grid (42 consecutive days) covering the currentMonth stored on the component.
   * @example
   * this.enenerateCalendarDays()
   * // this.calendarDays will be an array of objects like:
   * // [{ date: Date, dayNumber: number, isCurrentMonth: boolean, isOffDay: boolean, isPastDate: boolean }, ...]
   * @returns {void} Does not return a value; updates this.calendarDays on the component instance.
   **/
  generateCalendarDays(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    this.calendarDays = [];

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      this.calendarDays.push({
        date: date,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isOffDay: this.isOffDay(date),
        isPastDate: this.isPastDate(date),
      });
    }
  }

  getCurrentMonthYear(): string {
    return this.currentMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  previousMonth(): void {
    this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
    this.generateCalendarDays();
  }

  nextMonth(): void {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
    this.generateCalendarDays();
  }

  isSelectedDate(date: Date): boolean {
    if (!this.selectedDate) return false;
    return date.toDateString() === this.selectedDate.toDateString();
  }

  isToday(date: Date): boolean {
    const today = this.getShopToday();
    return !!today && date.toDateString() === today.toDateString();
  }

  onDateSelect(date: Date): void {
    // Don't allow selection of disabled dates (past dates or off days)
    if (this.isDateDisabled(date)) {
      return;
    }

    this.selectedDate = date;
    this.updateSelectedDateTime();
  }

  isTimeSlotSelected(slot: any): boolean {
    return this.selectedTime === slot.time;
  }

  onTimeSelect(slot: any): void {
    if (this.isTimeSlotAvailable(slot, this.selectedDate!)) {
      this.selectedTime = slot.time;
      this.updateSelectedDateTime();
    }
  }

  // Check if a time slot is available based on location hours
  /**
   * Determine if a given time slot is available on a specific date based on operating hours.
   * @example
   * sTimeSlotAvailable({time: '10:00', available: true}, new Date('2026-03-20'))
   * true
   * @param {{any}} {{slot}} - Slot object containing at least a time string (e.g., "10:00") and an available boolean fallback.
   * @param {{Date}} {{selectedDate}} - Date to check against operating hours; if falsy, function returns slot.available.
   * @returns {{boolean}} Whether the slot time falls within the day's operating hours and the day is open.
   **/
  isTimeSlotAvailable(slot: any, selectedDate: Date): boolean {
    if (!selectedDate) {
      return slot.available; // Fallback to original availability
    }

    const dayHours = this.getOperatingHoursForDay(selectedDate);

    if (!dayHours || dayHours.is_closed) {
      return false; // Day is closed
    }

    // Convert time to comparable format
    const slotTime = this.convertTimeToMinutes(slot.time);
    const openTime = this.convertTimeToMinutes(dayHours.open_time);
    const closeTime = this.convertTimeToMinutes(dayHours.close_time);

    if (slotTime < openTime || slotTime > closeTime) {
      return false;
    }

    if (this.isToday(selectedDate)) {
      const currentShopMinutes = this.getCurrentShopMinutes();
      if (currentShopMinutes == null || slotTime <= currentShopMinutes) {
        return false;
      }
    }

    return true;
  }

  private hasAvailableTimeSlot(date: Date): boolean {
    return this.timeSlots.some(slot => this.isTimeSlotAvailable(slot, date));
  }

  // Get operating hours for a specific day
  private getOperatingHoursForDay(date: Date): LocationHours | null {
    if (!this.locationHours || this.locationHours.length === 0) {
      return null;
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];

    return this.locationHours.find(hour => hour.day === dayName) || null;
  }

  // Convert time string to minutes for comparison
  /**
   * Converts a 12-hour time string with AM/PM to the total minutes since midnight.
   * @example
   * convertTimeToMinutes('02:30 PM')
   * 870
   * @param {{string}} {{timeString}} - Time string in the format "hh:mm AM" or "hh:mm PM".
   * @returns {{number}} Total minutes since midnight.
   **/
  private convertTimeToMinutes(timeString: string): number {
    const [time, period] = timeString.trim().split(' ');
    const [hours, minutes] = time.split(':').map(Number);

    let totalMinutes = hours * 60 + minutes;

    if (period === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    } else if (period === 'AM' && hours === 12) {
      totalMinutes -= 12 * 60;
    }

    return totalMinutes;
  }

  private updateSelectedDateTime(): void {
    if (this.selectedDate && this.selectedTime) {
      const [hours, minutes] = this.selectedTime.split(':').map(Number);
      const dateTime = this.createDateInLocationTimezone(this.selectedDate, hours, minutes);
      if (!dateTime) {
        return;
      }

      this.selectedDateTime = dateTime;
      this.onDateTimeChange(dateTime);
    }
  }

  private getLocationTimezone(): string {
    const timeZone = this.locationDetails?.timezone?.trim();
    if (!timeZone) {
      return '';
    }
    try {
      new Intl.DateTimeFormat('en-US', { timeZone }).format();
      return timeZone;
    } catch {
      return '';
    }
  }

  private getShopToday(): Date | null {
    const timeZone = this.getLocationTimezone();
    if (!timeZone) {
      return null;
    }
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(new Date())
      .reduce<Record<string, string>>((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});
    return new Date(Number(parts['year']), Number(parts['month']) - 1, Number(parts['day']));
  }

  private getCurrentShopMinutes(): number | null {
    const timeZone = this.getLocationTimezone();
    if (!timeZone) {
      return null;
    }
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .reduce<Record<string, string>>((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});
    return Number(parts['hour']) * 60 + Number(parts['minute']);
  }

  private createDateInLocationTimezone(date: Date, hours: number, minutes: number): Date | null {
    const timeZone = this.getLocationTimezone();
    if (!timeZone) {
      return null;
    }

    const wallTimeUtc = Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      0,
      0
    );
    let timestamp = wallTimeUtc;
    for (let i = 0; i < 2; i += 1) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date(timestamp))
        .reduce<Record<string, number>>((acc, part) => {
          if (part.type !== 'literal') {
            acc[part.type] = Number(part.value);
          }
          return acc;
        }, {});
      const zonedAsUtc = Date.UTC(
        parts['year'],
        parts['month'] - 1,
        parts['day'],
        parts['hour'],
        parts['minute'],
        parts['second'] ?? 0
      );
      timestamp = wallTimeUtc - (zonedAsUtc - timestamp);
    }
    return new Date(timestamp);
  }

  // Check if the selected date/time has changed from the original
  hasDateTimeChanged(): boolean {
    if (!this.selectedDateTime || !this.originalDateTime) {
      return false;
    }
    return this.selectedDateTime.getTime() !== this.originalDateTime.getTime();
  }

  onClose(): void {
    this.selectedDateTime = null;
    this.selectedDate = null;
    this.selectedTime = '';
    this.showOffDaysModal = false;
    this.pendingUpdate = false;
    this.closeModal.emit();
  }

  /**
   * Attempts to update the currently selected appointment date/time. If no date/time is selected or the appointment is null, logs a warning and aborts. If the selected date is an off day, shows the off-days modal and defers the update; otherwise proceeds with the update.
   * @example
   * nUpdate()
   * undefined
   * @returns {void} Performs side effects (shows modal, sets pending flag, or proceeds with the update) and does not return a value.
   **/
  onUpdate(): void {
    if (!this.selectedDateTime || !this.appointment) {
      console.warn('No date/time selected or appointment is null');
      return;
    }

    // Check if the selected date is on an off day
    if (this.isOffDay(this.selectedDateTime)) {
      this.showOffDaysModal = true;
      this.pendingUpdate = true;
      return;
    }

    // If not an off day, proceed with the update
    this.proceedWithUpdate();
  }

  // Handle off-days modal actions
  onChooseAnotherDay(): void {
    this.showOffDaysModal = false;
    this.pendingUpdate = false;
    // Keep the modal open for user to select another date
  }

  onProceedAnyway(): void {
    this.showOffDaysModal = false;
    this.proceedWithUpdate();
  }

  // Close the off-days modal
  onCloseOffDaysModal(): void {
    this.showOffDaysModal = false;
    this.pendingUpdate = false;
  }

  // Proceed with the actual update
  /**
   * Prepare and emit an appointment update payload using the selected date/time and current appointment; logs a warning and returns early if required data is missing.
   * @example
   * proceedWithUpdate()
   * undefined
   * @returns {void} Emits an 'updateAppointment' event with appointmentId and the constructed payload; does not return a value.
   */
  private proceedWithUpdate(): void {
    if (!this.selectedDateTime || !this.appointment) {
      console.warn('No date/time selected or appointment is null');
      return;
    }

    // Format the date time to ISO string
    const appointmentTime = this.selectedDateTime.toISOString();

    // Create the payload as specified in the requirements
    const payload = {
      appointment_status: 'confirmed',
      employee_id: this.appointment.employee_id,
      customer_id: this.appointment.customer_id,
      vehicle_id: this.appointment.vehicle_id, // Default vehicle ID as per requirements
      package_id: this.appointment.package_id,
      location_id: this.appointment.location_id,
      appointment_time: appointmentTime,
      source: 'web',
      duration: this.getAppointmentDuration(),
    };

    this.updateAppointment.emit({
      appointmentId: this.appointment.id,
      payload: payload,
    });
  }

  onDateTimeChange(dateTime: Date): void {
    this.selectedDateTime = dateTime;
  }

  // Initialize with current appointment data as default
  /**
   * Initialize the component state from the current appointment if one exists.
   * @example
   * initializeWithCurrentAppointment()
   * undefined
   * @returns {void} Sets selectedDate, currentMonth, selectedTime and originalDateTime, regenerates calendar days, and updates selected date/time when this.appointment is present.
   */
  private initializeWithCurrentAppointment(): void {
    if (this.appointment && !this.droppedDate) {
      // Set the selected date to the current appointment date
      const appointmentDate = new Date(this.appointment.appointment_time);
      this.selectedDate = new Date(
        appointmentDate.getFullYear(),
        appointmentDate.getMonth(),
        appointmentDate.getDate()
      );

      // Update the current month to show the appointment date
      this.currentMonth = new Date(appointmentDate.getFullYear(), appointmentDate.getMonth(), 1);
      this.generateCalendarDays();

      // Set the time to the original appointment time
      const timeString = `${appointmentDate.getHours().toString().padStart(2, '0')}:${appointmentDate.getMinutes().toString().padStart(2, '0')}`;
      this.selectedTime = timeString;

      // Update the selected date time
      this.updateSelectedDateTime();

      // Store the original date/time for comparison
      this.originalDateTime = new Date(appointmentDate);
    }
  }

  // Handle dropped date from drag and drop
  /**
   * Update component state when a date is dropped: set selectedDate, preserve original appointment time if not already saved, update currentMonth, regenerate calendar days, set selectedTime from the original appointment time when available, and update the combined selected date-time.
   * @example
   * handleDroppedDate()
   * undefined
   * @returns {{void}} Performs updates to the component's date/time state based on the dropped date.
   **/
  private handleDroppedDate(): void {
    if (this.droppedDate) {
      // Set the original date/time if not already set
      if (this.appointment && !this.originalDateTime) {
        const originalTime = new Date(this.appointment.appointment_time);
        this.originalDateTime = new Date(originalTime);
      }

      // Set the selected date to the dropped date
      this.selectedDate = new Date(this.droppedDate);

      // Update the current month to show the dropped date
      this.currentMonth = new Date(this.droppedDate.getFullYear(), this.droppedDate.getMonth(), 1);
      this.generateCalendarDays();

      // Set the time to the original appointment time if available
      if (this.appointment) {
        const originalTime = new Date(this.appointment.appointment_time);
        const timeString = `${originalTime.getHours().toString().padStart(2, '0')}:${originalTime.getMinutes().toString().padStart(2, '0')}`;
        this.selectedTime = timeString;
      }

      // Update the selected date time
      this.updateSelectedDateTime();
    }
  }

  // Prevent modal close when clicking inside the modal content
  onModalContentClick(event: Event): void {
    event.stopPropagation();
  }

  // Get formatted appointment duration
  /**
   * Return a human-readable appointment duration string based on this.appointment.duration, handling singular/plural and default fallback.
   * @example
   * etAppointmentDuration()
   * '1 hour'
   * @returns {string} Human-readable duration string (e.g., '1 hour', '30 minutes', or default '2 hours').
   */
  getAppointmentDuration(): string {
    if (!this.appointment || !this.appointment.duration) {
      return '2 hours'; // Default fallback
    }

    const { hours, minutes } = this.appointment.duration;

    if (hours && minutes) {
      if (hours === 1 && minutes === 0) {
        return '1 hour';
      } else if (hours === 1) {
        return `1 hour ${minutes} minutes`;
      } else if (minutes === 0) {
        return `${hours} hours`;
      } else {
        return `${hours} hours ${minutes} minutes`;
      }
    } else if (hours) {
      if (hours === 1) {
        return '1 hour';
      } else {
        return `${hours} hours`;
      }
    } else if (minutes) {
      if (minutes === 1) {
        return '1 minute';
      } else {
        return `${minutes} minutes`;
      }
    }

    return '2 hours'; // Default fallback
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
